<template>
  <div>
    <error-container type="general"></error-container>
    <template v-if="isLoading">
      <span class="fa fa-spinner fa-pulse fa-3x fa-fw d-block mx-auto mt-3"></span>
    </template>
    <template v-else>
      <b-row class="product-details__header product-details__section mb-3">
        <b-col md="4" class="product-details__product-image mb-3 mb-md-0">
          <b-img fluid :alt="productDetails.displayName" :src="productDetails.mediumImageUrl"/>
        </b-col>
        <b-col md="8">
          <div
            class="promo-flag promo-flag--left promo-flag--primo-pick promo-flag--relative"
            v-if="productDetails.isPrimoPick"
          >
            <div>Primo</div>
            <div>Pick</div>
          </div>

          <h1
            class="product-details__title h4 font-weight-bold pr-md-4"
          ><span v-html="productDetails.displayName"></span>, {{ productDetails.defaultSku.customerFriendlySize }}</h1>
          <div class="mb-2">
            <span class="text-muted h6">{{ productDetails.defaultSku.customerFriendlySize }}</span>
          </div>
          <div class="mb-2" v-if="productDetails.snipes && productDetails.snipes.length > 0">
            <div
              v-for="(snipe, index) in productDetails.snipes"
              :key="index"
              class="d-inline-block mr-2"
            >
              <img
                class="product-details__snipe-item"
                :src="snipe.largeImageUrl"
                :alt="snipe.name"
                :id="`snipe${index}`"
              >
              <b-popover
                :target="`snipe${index}`"
                triggers="hover focus"
                :title="snipe.name"
                :content="snipe.description"
                placement="auto"
              ></b-popover>
            </div>
          </div>
          <div class="mb-3">
            <div class="product-price">
                <span
                  class="font-weight-bold h4 m-0"
                  :class="{'product-price__strikethrough':productDetails.onSale}"
                >Regular: {{ productDetails.defaultSku.priceInfo.listPrice.formatted }}/{{ productDetails.defaultSku.priceInfo.listPrice.unit }}</span>
                <span v-if="productDetails.defaultSku.priceInfo.unitListPrice" class="text-muted">({{productDetails.defaultSku.priceInfo.unitListPrice.formatted}}/{{productDetails.defaultSku.priceInfo.unitListPrice.unit}})</span>
              <div v-if="productDetails.onSale" class="product-price__sale">
                <span
                  class="font-weight-bold h4 m-0 text-danger"
                >Sale: {{ productDetails.defaultSku.priceInfo.salePrice.formatted }}/{{ productDetails.defaultSku.priceInfo.salePrice.unit }}</span>
                <span v-if="productDetails.defaultSku.priceInfo.unitSalePrice" class="text-muted">({{productDetails.defaultSku.priceInfo.unitSalePrice.formatted}}/{{productDetails.defaultSku.priceInfo.unitSalePrice.unit}})</span>
              </div>
            </div>
          </div>
          <b-row>
            <b-col md="5">
              <preference-add-to-bucket
                v-if="displayPreferences"
                :product="productDetails"
                :bucket="bucket"
                size="large"
                listname="Product Detail Modal">
              </preference-add-to-bucket>
              <add-to-bucket
                v-else
                :product="productDetails"
                :bucket="bucket"
                size="large"
                listname="Product Detail Modal"
              ></add-to-bucket>
            </b-col>
          </b-row>
        </b-col>
      </b-row>

      <b-row class="product-details-body product-details__section pb-0">
        <b-col :md="isSidebarPopulated ? '8' : '12'">
          <section
            class="product-details-body__section mb-3"
            v-if="productDetails.longDescription && productDetails.longDescription.length > 0"
          >
            <collapsible-content :open="showCollapse" :disabled="showCollapse">
              <div class="section-header" slot="header">
                <h3 class="h5 font-weight-bold">Product Description</h3>
                <div v-if="!showCollapse" class="section-header__collapse-caret">
                  <span class="fa fa-chevron-down"></span>
                </div>
              </div>

              <div class="section-content" slot="body">
                <read-more>
                  <p class="mb-0" v-html="productDetails.longDescription"></p>
                </read-more>
              </div>
            </collapsible-content>
          </section>

          <section
            class="product-details-body__section mb-3"
            v-if="productDetails.preparationInstructions && productDetails.preparationInstructions.length > 0"
          >
            <collapsible-content :open="showCollapse" :disabled="showCollapse">
              <div class="section-header" slot="header">
                <h3 class="h5 font-weight-bold">Preparation Instructions</h3>
                <div v-if="!showCollapse" class="section-header__collapse-caret">
                  <span class="fa fa-chevron-down"></span>
                </div>
              </div>

              <div class="section-content" slot="body">
                <read-more>
                  <p class="mb-0" v-html="productDetails.preparationInstructions"></p>
                </read-more>
              </div>
            </collapsible-content>
          </section>

          <section
            class="product-details-body__section mb-3"
            v-if="productDetails.ingredients && productDetails.ingredients.length > 0"
          >
            <collapsible-content :open="showCollapse" :disabled="showCollapse">
              <div class="section-header" slot="header">
                <h3 class="h5 font-weight-bold">Ingredients</h3>
                <div v-if="!showCollapse" class="section-header__collapse-caret">
                  <span class="fa fa-chevron-down"></span>
                </div>
              </div>

              <div class="section-content" slot="body">
                <read-more>
                  <p class="mb-0" v-html="productDetails.ingredients"></p>
                </read-more>
              </div>
            </collapsible-content>
          </section>

          <section
            class="product-details-body__section mb-3"
            v-if="productDetails.safetyWarning && productDetails.safetyWarning.length > 0"
          >
            <collapsible-content :open="showCollapse" :disabled="showCollapse">
              <div class="section-header" slot="header">
                <h3 class="h5 font-weight-bold">Safety Warning</h3>
                <div v-if="!showCollapse" class="section-header__collapse-caret">
                  <span class="fa fa-chevron-down"></span>
                </div>
              </div>

              <div class="section-content" slot="body">
                <read-more>
                  <p class="mb-0" v-html="productDetails.safetyWarning"></p>
                </read-more>
              </div>
            </collapsible-content>
          </section>
        </b-col>

        <b-col md="4" v-if="isSidebarPopulated">
          <div
            v-if="productDetails.defaultSku.nutritionSummaries && productDetails.defaultSku.nutritionSummaries.length > 0"
          >
            <h3 class="h5 font-weight-bold">Nutrition Facts</h3>
            <div
              class="mb-3"
              v-if="productDetails.defaultSku.nutritionSummaries"
              v-for="(nutritionSummary, index) in productDetails.defaultSku.nutritionSummaries"
              :key="index"
            >
              <collapsible-content>
                <div slot="header" class="product-details-nutrition-panel__header text-white d-flex justify-content-between align-items-center p-2 mb-1">
                  <h4 class="h6 m-0 font-weight-bold">{{ productDetails.defaultSku.displayName }}</h4>
                  <span class="fa fa-chevron-down"></span>
                </div>

                <template slot="body">
                  <div
                    class="product-details-nutrition-panel__section border-0"
                    v-if="nutritionSummary.caloriesItems"
                  >
                    <span class="font-weight-bold">Amount Per Serving</span>
                    <div class="d-flex justify-content-between border-top pt-1 pb-1">
                      <div v-for="(caloriesItem, index) in nutritionSummary.caloriesItems" :key="index">
                        <span class="font-weight-bold">{{ caloriesItem.nutrition.name }}</span>
                        <span>{{ caloriesItem.nutritionValue }}</span>
                      </div>
                    </div>
                  </div>

                  <div
                    class="product-details-nutrition-panel__section"
                    v-if="nutritionSummary.otherMineralItems"
                  >
                    <div class="text-right">
                      <span class="font-weight-bold">% Daily Values*</span>
                    </div>
                    <div
                      class="d-flex justify-content-between border-top pt-1 pb-1"
                      v-for="(mineralItem, index) in nutritionSummary.otherMineralItems"
                      :key="index"
                    >
                      <div>
                        <span class="font-weight-bold">{{ mineralItem.nutrition.name }}</span>
                        <span>{{ mineralItem.nutritionValue }}{{ mineralItem.nutritionUom }}</span>
                      </div>
                      <div v-if="mineralItem.percentDailyIntakeValue">
                        <span>{{ mineralItem.percentDailyIntakeValue}}%</span>
                      </div>
                    </div>
                  </div>

                  <div
                    class="product-details-nutrition-panel__section product-details-nutrition-panel__section--heavy product-details-nutrition-panel__section--vitamins clearfix"
                    v-if="nutritionSummary.vitaminItems"
                  >
                    <div
                      class="product-details-nutrition-panel__vitamin-item"
                      v-for="(vitaminItem, index) in nutritionSummary.vitaminItems"
                      :key="index"
                    >
                      <span class="font-weight-bold">{{ vitaminItem.nutrition.name }}</span>
                      <span>{{ vitaminItem.nutritionValue }}{{ vitaminItem.nutritionUom }}</span>
                      <span>{{ vitaminItem.percentDailyIntakeValue}}%</span>
                    </div>
                  </div>
                </template>
              </collapsible-content>
            </div>
          </div>

          <div v-if="productDetails.guaranteeImageCode" class="text-center mb-3">
            <b-img fluid :src="productDetails.guaranteeImageUrl" alt="Product Guarantee" />
          </div>
        </b-col>
      </b-row>

      <div v-if="showSuggestedProducts && populatedSuggestedProducts.length > 0" class="product-details-recommended-products">
        <h2 class="h5 font-weight-bold section-title mt-3">We Recommend</h2>
        <div class="product-details__section">
          <product-collection-horizontal listname="We Recommend" productLinkDestination="modal" :size="productSize" :options="{showCoupons: false, showAddToList: false}" bucket='orderAddOns' :products="populatedSuggestedProducts" reco="personalized"></product-collection-horizontal>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import Errors from '../Errors'
import AddToBucket from '../AddToBucket'
import ProductCollectionHorizontal from './ProductCollectionHorizontal'
import CollapsibleContent from '../CollapsibleContent'
import ReadMore from '../ReadMore'
import PreferenceAddToBucket from '../preferences/PreferenceAddToBucket';

export default {
  name: 'ProductDetails',
  components: {
    'error-container': Errors,
    'add-to-bucket': AddToBucket,
    'product-collection-horizontal': ProductCollectionHorizontal,
    'collapsible-content': CollapsibleContent,
    'read-more': ReadMore,
    'preference-add-to-bucket': PreferenceAddToBucket,
  },
  props: {
    productId: {
      type: String
    },
    bucket: {
      type: String,
      default: 'cart' // cart, orderAddOns. Used for figuring out how many are in your bucket
    }
  },
  computed: {
    showCollapse () {
      return !window.heb.screen.isSmall
    },
    isLoading () {
      return this.$store.state.products.isLoading
    },
    showSuggestedProducts () {
      return this.bucket === 'orderAddOns'
    },
    populatedSuggestedProducts () {
      return this.$store.state.orderAddOns.upsells.suggestedProductRecommendations
    },
    storeId () {
      return this.$store.state.orderAddOns.orderAddOn.store.id || window.heb.data.user.currentStoreId || 108
    },
    productDetails () {
      return this.$store.state.products.productDetails
    },
    productSize () {
      return window.heb.screen.isSmall ? 'small' : 'normal'
    },
    bucketDisplayName () {
      switch (this.bucket) {
        default:
          return 'Cart'
        case 'cart':
          return 'Cart'
        case 'orderAddOns':
          return 'Order'
      }
    },
    isSidebarPopulated () {
      return (this.productDetails.defaultSku.nutritionSummaries && this.productDetails.defaultSku.nutritionSummaries.length > 0) || (this.productDetails.guaranteeImageCode)
    },
    displayPreferences() {
      return this.productDetails.purchasePreferenceList &&
        this.productDetails.purchasePreferenceList.purchasePreferences.length &&
        this.productDetails.addToCartAllowed;
    }
  },
  methods: {
    getSuggestedProducts () {
      if (this.showSuggestedProducts) {
        this.$store.dispatch('orderAddOns/GET_SUGGESTED_PRODUCT_RECOMMENDATIONS', this.$route.params.orderId)
      }
    }
  },
  mounted () {
    this.$store.dispatch('products/GET_PRODUCT_DETAILS', {productId: this.productId, storeId: this.storeId})
    this.getSuggestedProducts()
  },
  watch: {
    // whenever productId changes, this function will run
    productId: function (newId, oldID) {
      this.$store.dispatch('products/GET_PRODUCT_DETAILS', {productId: newId, storeId: this.storeId})
      this.getSuggestedProducts()
    }
  },
}
</script>

<style lang="scss">
// This component is standalone, and therefore needs curbside styles
@import "../../assets/lib";
@import "../../styles";

.product-details .modal-product-details .modal-header {
  position: absolute;
  right: 0;
  z-index: 1;
}

@media screen and (min-width: $breakpoint-tablet) {
  .product-details .modal-product-details {
    max-width: 848px;
  }
}

@media screen and (max-width: $breakpoint-tablet - 1) {
 .product-details .modal-product-details {
   .modal-content {
     border: 0;
     border-radius: 0;
     background: $color-background-light-gray;
   }

   .modal-body {
     padding-top: 0;
   }
 }
}

@media screen and (max-width:  $breakpoint-mobile - 1) {
  .product-details .modal-product-details {
    margin: 0;
  }
}
</style>

<style lang="scss" scoped>
@import "../../assets/lib";

.promo-flag--relative{
  position: relative;
  margin-bottom: 8px;
}

.section-header {
  position: relative;
  padding-right: 1em;

  &__collapse-caret {
    position: absolute;
    right: 0;
    top: 0;
    line-height: 1em;
    font-size: 0.7rem;
  }
}

.section-header .fa,
.product-details-nutrition-panel__header .fa {
  transform: rotate(0deg);
}

.collapsible-content--open .section-header .fa,
.collapsible-content--open .product-details-nutrition-panel__header .fa {
  transform: rotate(180deg);
}

.product-details__snipe-item {
  width: 24px;
  height: 24px;
  border-radius: 100%;
}

.product-details-nutrition-panel__header {
  background: #8b8b8b;
}

.product-details-nutrition-panel__section {
  font-size: 14px;

  border-top: 3px solid $color-border-standard-gray;

  &--heavy {
    border-top-width: 7px;
  }

  &--vitamins {
    &:after {
      // This helps allow for uneven vitamin-items by always including a bottom border
      display: block;
      position: relative;
      top: -1px;
      content: "";
      border-bottom: 1px solid $color-border-standard-gray;
    }
  }
}

.product-details-nutrition-panel__vitamin-item {
  position: relative;
  float: left;
  width: 50%;

  &:nth-child(2n) {
    text-align: right;

    &:before {
      display: block;
      content: "•";
      position: absolute;
      left: 0;
    }
  }

  &:after {
    display: block;
    content: "";
    clear: both;
    border-top: 1px solid $color-border-standard-gray;
  }
}

.product-price .text-muted {
  padding-left:8px;
}

.product-price__strikethrough{
  font-size: 1rem;
  text-decoration: line-through;
}

@media screen and (max-width: $breakpoint-tablet - 1) {
  .product-details__section {
    background: #fff;
    box-shadow: 0 0 4px rgba(#000, 0.1);
    padding-top: $padding-standard;
    padding-bottom: $padding-standard;
  }

  .product-details-recommended-products .product-details__section {
    margin-left: -15px;
    margin-right: -15px;
  }

  .product-details__product-image {
    max-height: 200px;
    text-align: center;

    img {
      height: 100%;
      width: auto;
      max-height: 200px;
    }
  }
}
</style>
